import React, { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import * as XLSX from 'xlsx';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

/**
 * AdminDashboard Component
 * Displays customer data, analytics, and visualizations
 */
const AdminDashboard = () => {
  const [customers, setCustomers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [giftStats, setGiftStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isAmazonStatsExpanded, setIsAmazonStatsExpanded] = useState(false);
  const [isFunnelAmazonExpanded, setIsFunnelAmazonExpanded] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('FR');

  // Review verification state
  const [amazonReviews, setAmazonReviews] = useState(null);
  const [reviewMatches, setReviewMatches] = useState(null);

  // Fetch customers and calculate analytics
  useEffect(() => {
    fetchCustomersAndAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegion]);

  // Export customers to CSV
  const exportToCSV = () => {
    if (customers.length === 0) {
      alert('No customers to export');
      return;
    }

    // Create CSV headers
    const headers = [
      'ID',
      'Created At',
      'First Name',
      'Last Name',
      'Email',
      'Opt-in Surveys',
      'Review Generated',
      'Review Stars',
      'Review Tone',
      'Review Text',
      'Went to Amazon',
      'Claimed Gifts'
    ];

    // Create CSV rows
    const rows = customers.map(customer => [
      customer.id,
      new Date(customer.created_at).toISOString(),
      customer.first_name,
      customer.last_name,
      customer.email,
      customer.opt_in_surveys ? 'Yes' : 'No',
      customer.review_generated ? 'Yes' : 'No',
      customer.review_stars || '',
      customer.review_tone || '',
      customer.review_text ? `"${customer.review_text.replace(/"/g, '""')}"` : '',
      customer.went_to_amazon ? 'Yes' : 'No',
      customer.claimed_gifts ? 'Yes' : 'No'
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `customers_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Toggle customer selection
  const toggleCustomerSelection = (customerId) => {
    setSelectedCustomers(prev => {
      if (prev.includes(customerId)) {
        return prev.filter(id => id !== customerId);
      } else {
        return [...prev, customerId];
      }
    });
  };

  // Toggle all customers selection
  const toggleAllCustomers = () => {
    if (selectedCustomers.length === customers.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(customers.map(c => c.id));
    }
  };

  // Delete selected customers
  const deleteSelectedCustomers = async () => {
    if (selectedCustomers.length === 0) {
      alert('Aucun client sélectionné');
      return;
    }

    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedCustomers.length} client(s) ? Ils seront déplacés vers la corbeille.`)) {
      return;
    }

    try {
      if (!supabase) {
        alert('Supabase non configuré');
        return;
      }

      // Process each customer
      for (const customerId of selectedCustomers) {
        // Fetch customer data
        const { data: customerData, error: fetchError } = await supabase
          .from('customer_submissions')
          .select('*')
          .eq('id', customerId)
          .single();

        if (fetchError) {
          console.error('Error fetching customer:', fetchError);
          continue;
        }

        // Insert into trash table
        const { error: insertError } = await supabase
          .from('deleted_customer_submissions')
          .insert({
            original_id: customerData.id,
            created_at: customerData.created_at,
            first_name: customerData.first_name,
            last_name: customerData.last_name,
            email: customerData.email,
            opt_in_surveys: customerData.opt_in_surveys,
            review_generated: customerData.review_generated,
            review_stars: customerData.review_stars,
            review_tone: customerData.review_tone,
            review_text: customerData.review_text,
            went_to_amazon: customerData.went_to_amazon,
            claimed_gifts: customerData.claimed_gifts
          });

        if (insertError) {
          console.error('Error inserting to trash:', insertError);
          continue;
        }

        // Delete associated gift downloads
        await supabase
          .from('gift_downloads')
          .delete()
          .eq('customer_id', customerId);

        // Delete from main table
        const { error: deleteError } = await supabase
          .from('customer_submissions')
          .delete()
          .eq('id', customerId);

        if (deleteError) {
          console.error('Error deleting customer:', deleteError);
        }
      }

      // Clear selection and refresh
      setSelectedCustomers([]);
      await fetchCustomersAndAnalytics();
      alert(`${selectedCustomers.length} client(s) supprimé(s) avec succès`);
    } catch (error) {
      console.error('Error deleting customers:', error);
      alert('Échec de la suppression: ' + error.message);
    }
  };

  const fetchCustomersAndAnalytics = async () => {
    setIsLoading(true);
    setError('');

    try {
      // Check if Supabase is configured
      if (!supabase) {
        // Demo mode - generate mock data
        const mockCustomers = generateMockData();
        setCustomers(mockCustomers);
        setAnalytics(calculateAnalytics(mockCustomers));
        await fetchGiftStats(); // Fetch gift stats in demo mode too
        setIsLoading(false);
        return;
      }

      // Fetch all customer submissions from Supabase filtered by region
      const { data, error: fetchError } = await supabase
        .from('customer_submissions')
        .select('*')
        .eq('region', selectedRegion)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setCustomers(data || []);
      setAnalytics(calculateAnalytics(data || []));

      // Fetch gift download stats
      await fetchGiftStats();
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load dashboard data. ' + err.message);
      // Still show mock data on error
      const mockCustomers = generateMockData();
      setCustomers(mockCustomers);
      setAnalytics(calculateAnalytics(mockCustomers));
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch gift download statistics
  const fetchGiftStats = async () => {
    if (!supabase) {
      // Demo mode
      setGiftStats({
        spotify_playlist: 5,
        game_guide: 8,
        relationship_ebook: 6
      });
      return;
    }

    try {
      const { data, error } = await supabase
        .from('gift_downloads')
        .select('gift_type, customer_id')
        .eq('region', selectedRegion);

      if (error) throw error;

      // Count downloads by type - total and unique users
      const stats = {
        spotify_playlist: { total: 0, unique: 0 },
        game_guide: { total: 0, unique: 0 },
        relationship_ebook: { total: 0, unique: 0 }
      };

      // Track unique users per gift type
      const uniqueUsers = {
        spotify_playlist: new Set(),
        game_guide: new Set(),
        relationship_ebook: new Set()
      };

      data.forEach(download => {
        if (stats.hasOwnProperty(download.gift_type)) {
          stats[download.gift_type].total++;
          if (download.customer_id) {
            uniqueUsers[download.gift_type].add(download.customer_id);
          }
        }
      });

      // Set unique counts
      Object.keys(stats).forEach(type => {
        stats[type].unique = uniqueUsers[type].size;
      });

      setGiftStats(stats);
    } catch (err) {
      console.error('Error fetching gift stats:', err);
      setGiftStats({
        spotify_playlist: { total: 0, unique: 0 },
        game_guide: { total: 0, unique: 0 },
        relationship_ebook: { total: 0, unique: 0 }
      });
    }
  };

  // --- Vérification Reviews: CSV/XLSX parsing & matching ---

  const parseCSV = (text) => {
    const lines = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      if (ch === '"') {
        if (inQuotes && text[i + 1] === '"') { current += '"'; i++; }
        else { inQuotes = !inQuotes; }
      } else if (ch === ',' && !inQuotes) {
        lines.push(current); current = '';
      } else if ((ch === '\n' || ch === '\r') && !inQuotes) {
        if (current || lines.length > 0) { lines.push(current); current = ''; }
        if (lines.length > 0) return { line: lines, rest: text.slice(i + (text[i + 1] === '\n' ? 2 : 1)) };
      } else { current += ch; }
    }
    if (current || lines.length > 0) lines.push(current);
    return { line: lines, rest: '' };
  };

  const parseFullCSV = (text) => {
    const rows = [];
    let remaining = text.replace(/^\uFEFF/, '');
    while (remaining.length > 0) {
      const { line, rest } = parseCSV(remaining);
      if (line.length > 0) rows.push(line);
      if (rest === remaining) break;
      remaining = rest;
    }
    if (rows.length < 2) return [];
    const headers = rows[0].map(h => h.trim());
    return rows.slice(1).map(row => {
      const obj = {};
      headers.forEach((h, i) => { obj[h] = (row[i] || '').trim(); });
      return obj;
    });
  };

  const normalizeText = (text) => {
    return (text || '').toLowerCase().replace(/[^a-z0-9\s\u00e0-\u00ff]/g, '').replace(/\s+/g, ' ').trim();
  };

  const jaccardSimilarity = (text1, text2) => {
    const words1 = new Set(normalizeText(text1).split(' ').filter(w => w.length > 2));
    const words2 = new Set(normalizeText(text2).split(' ').filter(w => w.length > 2));
    if (words1.size === 0 || words2.size === 0) return 0;
    const intersection = new Set([...words1].filter(w => words2.has(w)));
    const union = new Set([...words1, ...words2]);
    return intersection.size / union.size;
  };

  const processReviewRows = (parsed) => {
    if (!parsed || parsed.length === 0) { alert('Aucune donnée trouvée dans le fichier.'); return; }
    const keys = Object.keys(parsed[0] || {});
    const reviewCol = keys.find(k =>
      k.toLowerCase().includes('reviewdescription') || k.toLowerCase().includes('review_text') ||
      k.toLowerCase().includes('review text') || k.toLowerCase().includes('body') || k.toLowerCase().includes('content')
    );
    const ratingCol = keys.find(k => k.toLowerCase().includes('ratingscore') || k.toLowerCase().includes('rating') || k.toLowerCase().includes('stars'));
    const titleCol = keys.find(k => k.toLowerCase().includes('reviewtitle') || k.toLowerCase().includes('title'));
    const dateCol = keys.find(k => k === 'date' || k.toLowerCase().includes('date'));
    if (!reviewCol) { alert('Colonne de review non trouvée. Attendu: reviewDescription, review_text, body, ou content.\n\nColonnes trouvées: ' + keys.join(', ')); return; }
    const reviews = parsed
      .filter(row => row[reviewCol] && String(row[reviewCol]).trim().length > 0)
      .map(row => ({ text: String(row[reviewCol]), rating: ratingCol ? parseInt(row[ratingCol]) : null, title: titleCol ? String(row[titleCol] || '') : '', date: dateCol ? String(row[dateCol] || '') : '' }));
    setAmazonReviews(reviews);
    matchReviews(reviews);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
    if (isExcel) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const workbook = XLSX.read(e.target.result, { type: 'array' });
        const parsed = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]]);
        processReviewRows(parsed);
      };
      reader.readAsArrayBuffer(file);
    } else {
      const reader = new FileReader();
      reader.onload = (e) => processReviewRows(parseFullCSV(e.target.result));
      reader.readAsText(file);
    }
    event.target.value = '';
  };

  const matchReviews = (amzReviews) => {
    const candidates = customers.filter(c => c.review_text && c.went_to_amazon);
    const matches = candidates.map(customer => {
      let bestMatch = null, bestScore = 0;
      amzReviews.forEach(amzReview => {
        const score = jaccardSimilarity(customer.review_text, amzReview.text);
        if (score > bestScore) { bestScore = score; bestMatch = amzReview; }
      });
      let status = 'not_found';
      if (bestScore >= 0.7) status = 'confirmed';
      else if (bestScore >= 0.4) status = 'probable';
      return { customer, bestMatch, score: bestScore, status, starsMatch: bestMatch && bestMatch.rating ? customer.review_stars === bestMatch.rating : null };
    });
    matches.sort((a, b) => {
      const order = { confirmed: 0, probable: 1, not_found: 2 };
      return order[a.status] - order[b.status] || b.score - a.score;
    });
    setReviewMatches(matches);
  };

  // Calculate analytics metrics
  const calculateAnalytics = (data) => {
    const total = data.length;
    const reachedReviewPage = data.filter(c => c.review_generated).length;
    const wentToAmazon = data.filter(c => c.went_to_amazon).length;
    const claimedGifts = data.filter(c => c.claimed_gifts).length;
    const claimedAfterAmazon = data.filter(c => c.went_to_amazon && c.claimed_gifts).length;
    const claimedWithoutAmazon = data.filter(c => !c.went_to_amazon && c.claimed_gifts).length;

    // NEW: Reviews likely submitted on Amazon (generated + clicked Amazon)
    const likelySubmittedToAmazon = data.filter(c => c.review_generated && c.went_to_amazon).length;

    // Average star rating
    const reviewsWithStars = data.filter(c => c.review_stars);
    const avgStars = reviewsWithStars.length > 0
      ? (reviewsWithStars.reduce((sum, c) => sum + c.review_stars, 0) / reviewsWithStars.length).toFixed(1)
      : 0;

    // Tone distribution
    const toneDistribution = {};
    data.forEach(c => {
      if (c.review_tone) {
        toneDistribution[c.review_tone] = (toneDistribution[c.review_tone] || 0) + 1;
      }
    });

    return {
      total,
      reachedReviewPage,
      wentToAmazon,
      claimedGifts,
      claimedAfterAmazon,
      claimedWithoutAmazon,
      likelySubmittedToAmazon,
      avgStars,
      toneDistribution,
      conversionRate: total > 0 ? ((wentToAmazon / total) * 100).toFixed(1) : 0,
      giftsClaimRate: total > 0 ? ((claimedGifts / total) * 100).toFixed(1) : 0,
      amazonSubmissionRate: total > 0 ? ((likelySubmittedToAmazon / total) * 100).toFixed(1) : 0
    };
  };

  // Generate mock data for demo mode
  const generateMockData = () => {
    return [
      {
        id: 'mock-1',
        created_at: new Date(Date.now() - 3600000).toISOString(),
        first_name: 'John',
        last_name: 'Doe',
        email: 'john@example.com',
        opt_in_surveys: true,
        review_generated: true,
        review_stars: 5,
        review_tone: 'Enthusiastic',
        review_text: 'This game is amazing! Perfect for date night.',
        went_to_amazon: true,
        claimed_gifts: true
      },
      {
        id: 'mock-2',
        created_at: new Date(Date.now() - 7200000).toISOString(),
        first_name: 'Jane',
        last_name: 'Smith',
        email: 'jane@example.com',
        opt_in_surveys: false,
        review_generated: true,
        review_stars: 4,
        review_tone: 'Romantic',
        review_text: 'My partner and I loved it! Sweet and fun.',
        went_to_amazon: false,
        claimed_gifts: true
      },
      {
        id: 'mock-3',
        created_at: new Date(Date.now() - 10800000).toISOString(),
        first_name: 'Mike',
        last_name: 'Johnson',
        email: 'mike@example.com',
        opt_in_surveys: true,
        review_generated: false,
        review_stars: null,
        review_tone: null,
        review_text: null,
        went_to_amazon: false,
        claimed_gifts: false
      }
    ];
  };

  // Colors for charts
  const COLORS = ['#000000', '#333333', '#666666', '#999999'];

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <svg
            className="animate-spin h-12 w-12 text-black mx-auto mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <p className="text-lg font-light">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-light tracking-wide mb-4">
            Admin Dashboard
          </h1>
          <p className="text-lg text-gray-600">
            Customer analytics and review data
          </p>
          {!supabase && (
            <p className="mt-2 text-sm text-yellow-600 italic">
              Demo Mode - Showing sample data
            </p>
          )}

          {/* Region Toggle */}
          <div className="mt-6 flex justify-center items-center gap-4">
            <span className="text-sm font-light text-gray-600">Marché:</span>
            <div className="flex border border-gray-300">
              <button
                onClick={() => setSelectedRegion('US')}
                className={`px-6 py-2 text-sm font-light tracking-wide transition-all duration-300 ${
                  selectedRegion === 'US'
                    ? 'bg-black text-white'
                    : 'bg-white text-black hover:bg-gray-50'
                }`}
              >
                🇺🇸 US
              </button>
              <button
                onClick={() => setSelectedRegion('FR')}
                className={`px-6 py-2 text-sm font-light tracking-wide transition-all duration-300 ${
                  selectedRegion === 'FR'
                    ? 'bg-black text-white'
                    : 'bg-white text-black hover:bg-gray-50'
                }`}
              >
                🇫🇷 FR
              </button>
            </div>
          </div>

          <div className="mt-6">
            <button
              onClick={exportToCSV}
              disabled={customers.length === 0}
              className="px-6 py-3 bg-black text-white text-sm font-light tracking-wide
                       hover:bg-gray-800 transition-all duration-300
                       focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2
                       disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Export to CSV
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-8 p-4 bg-yellow-50 border border-yellow-200">
            <p className="text-sm text-yellow-800">{error}</p>
          </div>
        )}

        {/* Analytics Summary Cards */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="bg-white p-6 border border-gray-200">
              <h3 className="text-sm font-light tracking-wide text-gray-600 mb-2">
                Total Clients
              </h3>
              <p className="text-4xl font-light">{analytics.total}</p>
            </div>

            <div className="bg-white p-6 border border-gray-200">
              <h3 className="text-sm font-light tracking-wide text-gray-600 mb-2">
                Cadeaux Réclamés
              </h3>
              <p className="text-4xl font-light">{analytics.claimedGifts}</p>
              <p className="text-xs text-gray-500 mt-1">
                {analytics.giftsClaimRate}% taux de réclamation
              </p>
            </div>

            <div
              className="bg-white p-6 border border-gray-200 bg-green-50 border-green-300 cursor-pointer hover:shadow-md transition-shadow duration-200"
              onClick={() => setIsAmazonStatsExpanded(!isAmazonStatsExpanded)}
            >
              <div className="flex justify-between items-start">
                <h3 className="text-sm font-light tracking-wide text-gray-600 mb-2">
                  Reviews Soumises Amazon
                </h3>
                <svg
                  className={`w-5 h-5 text-gray-600 transition-transform duration-200 ${isAmazonStatsExpanded ? 'transform rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              <p className="text-4xl font-light text-green-700">{analytics.likelySubmittedToAmazon}</p>
              <p className="text-xs text-gray-500 mt-1">
                {analytics.amazonSubmissionRate}% taux de soumission
              </p>
              <p className="text-xs text-gray-500 mt-1 italic">
                (Généré + Clic Amazon)
              </p>

              {/* Détails dépliables */}
              {isAmazonStatsExpanded && (
                <div className="mt-4 pt-4 border-t border-green-200 space-y-3 animate-fade-in">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">Reviews Générés (info)</span>
                    <span className="text-sm font-light text-gray-700">{analytics.reachedReviewPage}</span>
                  </div>
                  <div className="w-full bg-gray-200 h-2 relative">
                    <div
                      className="absolute inset-0 bg-gray-400"
                      style={{ width: `${analytics.total > 0 ? (analytics.reachedReviewPage / analytics.total * 100) : 0}%` }}
                    ></div>
                  </div>
                  <div className="text-xs text-gray-500 text-right">
                    {analytics.total > 0 ? ((analytics.reachedReviewPage / analytics.total) * 100).toFixed(0) : 0}%
                  </div>

                  <div className="flex justify-between items-center mt-3">
                    <span className="text-xs text-gray-600">Clics Amazon (info)</span>
                    <span className="text-sm font-light text-gray-700">{analytics.wentToAmazon}</span>
                  </div>
                  <div className="w-full bg-gray-200 h-2 relative">
                    <div
                      className="absolute inset-0 bg-gray-400"
                      style={{ width: `${analytics.total > 0 ? (analytics.wentToAmazon / analytics.total * 100) : 0}%` }}
                    ></div>
                  </div>
                  <div className="text-xs text-gray-500 text-right">
                    {analytics.total > 0 ? ((analytics.wentToAmazon / analytics.total) * 100).toFixed(0) : 0}%
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white p-6 border border-gray-200">
              <h3 className="text-sm font-light tracking-wide text-gray-600 mb-2">
                Reviews Générés
              </h3>
              <p className="text-4xl font-light">{analytics.reachedReviewPage}</p>
              <p className="text-xs text-gray-500 mt-1">
                Note Moyenne: {analytics.avgStars} ★
              </p>
            </div>
          </div>
        )}

        {/* Charts */}
        {analytics && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Funnel Chart */}
            <div className="bg-white p-6 border border-gray-200">
              <h3 className="text-xl font-light tracking-wide mb-6">Entonnoir de Conversion</h3>
              <div className="space-y-4">
                {/* Soumissions (Base 100%) */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-light text-gray-700">1. Informations Soumises</span>
                    <span className="text-sm font-light text-gray-900">{analytics.total}</span>
                  </div>
                  <div className="w-full bg-gray-200 h-12 relative flex items-center justify-center">
                    <div className="absolute inset-0 bg-black" style={{ width: '100%' }}></div>
                    <span className="relative z-10 text-white text-sm font-light">100%</span>
                  </div>
                </div>

                {/* Cadeaux Réclamés */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-light text-gray-700">2. Cadeaux Réclamés</span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-light text-gray-900">{analytics.claimedGifts}</span>
                      <span className="text-xs text-red-600 font-light">
                        -{analytics.total - analytics.claimedGifts} abandons
                      </span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 h-12 relative flex items-center justify-center">
                    <div
                      className="absolute inset-0 bg-black"
                      style={{ width: `${analytics.total > 0 ? (analytics.claimedGifts / analytics.total * 100) : 0}%` }}
                    ></div>
                    <span className="relative z-10 text-sm font-light" style={{
                      color: (analytics.claimedGifts / analytics.total) > 0.5 ? 'white' : 'black'
                    }}>
                      {analytics.total > 0 ? ((analytics.claimedGifts / analytics.total) * 100).toFixed(0) : 0}%
                    </span>
                  </div>
                </div>

                {/* Reviews Probablement Soumises sur Amazon - IMPORTANT - Cliquable */}
                <div className="pt-4 border-t border-gray-200">
                  <div
                    className="flex justify-between items-center mb-2 cursor-pointer hover:bg-green-50 p-2 -m-2 rounded transition-colors duration-200"
                    onClick={() => setIsFunnelAmazonExpanded(!isFunnelAmazonExpanded)}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-light text-green-700 font-medium">Reviews Soumises Amazon <span className="text-xs italic font-normal">(généré + clic)</span></span>
                      <svg
                        className={`w-4 h-4 text-green-700 transition-transform duration-200 ${isFunnelAmazonExpanded ? 'transform rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                    <span className="text-sm font-light text-green-700 font-medium">{analytics.likelySubmittedToAmazon}</span>
                  </div>
                  <div className="w-full bg-gray-200 h-10 relative flex items-center justify-center border-2 border-green-500">
                    <div
                      className="absolute inset-0 bg-green-600"
                      style={{ width: `${analytics.total > 0 ? (analytics.likelySubmittedToAmazon / analytics.total * 100) : 0}%` }}
                    ></div>
                    <span className="relative z-10 text-sm font-light font-medium" style={{
                      color: (analytics.likelySubmittedToAmazon / analytics.total) > 0.5 ? 'white' : 'black'
                    }}>
                      {analytics.total > 0 ? ((analytics.likelySubmittedToAmazon / analytics.total) * 100).toFixed(0) : 0}%
                    </span>
                  </div>

                  {/* Détails dépliables - Reviews Générés et Clics Amazon */}
                  {isFunnelAmazonExpanded && (
                    <div className="mt-4 pt-4 border-t border-green-200 space-y-4 animate-fade-in">
                      {/* Reviews Générés (Optionnel) - PETIT */}
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-light text-gray-500">📝 Reviews Générés <span className="text-xs italic">(info)</span></span>
                          <span className="text-xs font-light text-gray-600">{analytics.reachedReviewPage}</span>
                        </div>
                        <div className="w-full bg-gray-200 h-6 relative flex items-center justify-center">
                          <div
                            className="absolute inset-0 bg-gray-400"
                            style={{ width: `${analytics.total > 0 ? (analytics.reachedReviewPage / analytics.total * 100) : 0}%` }}
                          ></div>
                          <span className="relative z-10 text-xs font-light" style={{
                            color: (analytics.reachedReviewPage / analytics.total) > 0.5 ? 'white' : 'black'
                          }}>
                            {analytics.total > 0 ? ((analytics.reachedReviewPage / analytics.total) * 100).toFixed(0) : 0}%
                          </span>
                        </div>
                      </div>

                      {/* Clics Amazon (Optionnel) - PETIT */}
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-light text-gray-500">🛒 Clics Amazon <span className="text-xs italic">(info)</span></span>
                          <span className="text-xs font-light text-gray-600">{analytics.wentToAmazon}</span>
                        </div>
                        <div className="w-full bg-gray-200 h-6 relative flex items-center justify-center">
                          <div
                            className="absolute inset-0 bg-gray-400"
                            style={{ width: `${analytics.total > 0 ? (analytics.wentToAmazon / analytics.total * 100) : 0}%` }}
                          ></div>
                          <span className="relative z-10 text-xs font-light" style={{
                            color: (analytics.wentToAmazon / analytics.total) > 0.5 ? 'white' : 'black'
                          }}>
                            {analytics.total > 0 ? ((analytics.wentToAmazon / analytics.total) * 100).toFixed(0) : 0}%
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Key Insight */}
                <div className="mt-6 p-4 bg-gray-50 border border-gray-200">
                  <p className="text-xs font-light text-gray-600 leading-relaxed">
                    <strong>Point clé:</strong> {analytics.claimedWithoutAmazon} clients ({analytics.total > 0 ? ((analytics.claimedWithoutAmazon / analytics.total) * 100).toFixed(0) : 0}%) ont réclamé leurs cadeaux sans passer par Amazon. Les cadeaux ne sont pas conditionnés aux reviews.
                  </p>
                  <p className="text-xs font-light text-gray-600 leading-relaxed mt-2">
                    <strong>Reviews Amazon:</strong> {analytics.likelySubmittedToAmazon} clients ({analytics.amazonSubmissionRate}%) ont probablement soumis leur avis sur Amazon (généré + clic Amazon).
                  </p>
                </div>
              </div>
            </div>

            {/* Gift Claim Distribution */}
            <div className="bg-white p-6 border border-gray-200">
              <h3 className="text-xl font-light tracking-wide mb-6">Gift Claim Breakdown</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'After Amazon', value: analytics.claimedAfterAmazon },
                      { name: 'Without Amazon', value: analytics.claimedWithoutAmazon },
                      { name: 'No Gifts Claimed', value: analytics.total - analytics.claimedGifts }
                    ]}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ percent }) => percent > 0 ? `${(percent * 100).toFixed(0)}%` : ''}
                  >
                    {[0, 1, 2].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Gift Downloads Stats */}
        {giftStats && (
          <div className="mb-12">
            <h2 className="text-2xl font-light tracking-wide mb-6">Gift Downloads</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                  </svg>
                </div>
                <h3 className="text-sm font-light tracking-wide text-gray-600 mb-2">
                  Spotify Playlist
                </h3>
                <p className="text-4xl font-light mb-1">{giftStats.spotify_playlist.unique}</p>
                <p className="text-xs text-gray-500">Clics uniques ({giftStats.spotify_playlist.total} totaux)</p>
              </div>

              <div className="bg-white p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
                  </svg>
                </div>
                <h3 className="text-sm font-light tracking-wide text-gray-600 mb-2">
                  Game Guide
                </h3>
                <p className="text-4xl font-light mb-1">{giftStats.game_guide.unique}</p>
                <p className="text-xs text-gray-500">Clics uniques ({giftStats.game_guide.total} totaux)</p>
              </div>

              <div className="bg-white p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                  </svg>
                </div>
                <h3 className="text-sm font-light tracking-wide text-gray-600 mb-2">
                  Relationship eBook
                </h3>
                <p className="text-4xl font-light mb-1">{giftStats.relationship_ebook.unique}</p>
                <p className="text-xs text-gray-500">Clics uniques ({giftStats.relationship_ebook.total} totaux)</p>
              </div>
            </div>
          </div>
        )}

        {/* Vérification Reviews */}
        <div className="mb-12">
          <div className="bg-white p-6 border border-gray-200">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-light tracking-wide">Vérification Reviews</h2>
              <label className="px-6 py-2 bg-black text-white text-sm font-light tracking-wide cursor-pointer hover:bg-gray-800 transition-all duration-300">
                Upload Reviews Amazon (CSV/XLSX)
                <input type="file" accept=".csv,.xlsx,.xls" onChange={handleFileUpload} className="hidden" />
              </label>
            </div>

            {!amazonReviews && (
              <p className="text-sm text-gray-500 font-light">
                Uploadez un fichier CSV ou XLSX avec les reviews Amazon (SellerSprite ou similaire) pour vérifier quels clients ont réellement soumis leur review.
              </p>
            )}

            {amazonReviews && reviewMatches && (
              <>
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="p-4 border border-gray-200 text-center">
                    <p className="text-2xl font-light">{amazonReviews.length}</p>
                    <p className="text-xs text-gray-500">Reviews Amazon</p>
                  </div>
                  <div className="p-4 border border-green-300 bg-green-50 text-center">
                    <p className="text-2xl font-light text-green-700">{reviewMatches.filter(m => m.status === 'confirmed').length}</p>
                    <p className="text-xs text-green-600">Confirmées</p>
                  </div>
                  <div className="p-4 border border-yellow-300 bg-yellow-50 text-center">
                    <p className="text-2xl font-light text-yellow-700">{reviewMatches.filter(m => m.status === 'probable').length}</p>
                    <p className="text-xs text-yellow-600">Probables</p>
                  </div>
                  <div className="p-4 border border-gray-200 text-center">
                    <p className="text-2xl font-light text-gray-500">{reviewMatches.filter(m => m.status === 'not_found').length}</p>
                    <p className="text-xs text-gray-500">Non trouvées</p>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="px-4 py-3 text-left text-xs font-light tracking-wide text-gray-600">Client</th>
                        <th className="px-4 py-3 text-left text-xs font-light tracking-wide text-gray-600">Review Générée</th>
                        <th className="px-4 py-3 text-left text-xs font-light tracking-wide text-gray-600">Meilleur Match Amazon</th>
                        <th className="px-4 py-3 text-center text-xs font-light tracking-wide text-gray-600">Score</th>
                        <th className="px-4 py-3 text-center text-xs font-light tracking-wide text-gray-600">Étoiles</th>
                        <th className="px-4 py-3 text-center text-xs font-light tracking-wide text-gray-600">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reviewMatches.map((match, i) => (
                        <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium">{match.customer.first_name} {match.customer.last_name}</p>
                            <p className="text-xs text-gray-500">{match.customer.email}</p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-xs text-gray-700 max-w-xs" title={match.customer.review_text}>
                              {match.customer.review_text.length > 120 ? match.customer.review_text.substring(0, 120) + '...' : match.customer.review_text}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">{match.customer.review_stars}★</p>
                          </td>
                          <td className="px-4 py-3">
                            {match.bestMatch ? (
                              <>
                                <p className="text-xs text-gray-700 max-w-xs" title={match.bestMatch.text}>
                                  {match.bestMatch.text.length > 120 ? match.bestMatch.text.substring(0, 120) + '...' : match.bestMatch.text}
                                </p>
                                {match.bestMatch.date && <p className="text-xs text-gray-400 mt-1">{match.bestMatch.date}</p>}
                              </>
                            ) : (
                              <p className="text-xs text-gray-400 italic">Aucun match</p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center"><span className="text-sm font-light">{(match.score * 100).toFixed(0)}%</span></td>
                          <td className="px-4 py-3 text-center">
                            {match.starsMatch === true && <span className="text-green-600 text-sm">✓</span>}
                            {match.starsMatch === false && <span className="text-red-500 text-sm">✗</span>}
                            {match.starsMatch === null && <span className="text-gray-400 text-sm">—</span>}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {match.status === 'confirmed' && <span className="px-3 py-1 text-xs font-light bg-green-100 text-green-800 border border-green-300">Confirmée</span>}
                            {match.status === 'probable' && <span className="px-3 py-1 text-xs font-light bg-yellow-100 text-yellow-800 border border-yellow-300">Probable</span>}
                            {match.status === 'not_found' && <span className="px-3 py-1 text-xs font-light bg-gray-100 text-gray-600 border border-gray-300">Non trouvée</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Customer List */}
        <div className="bg-white border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-light tracking-wide">Customer List</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {customers.length} total customer{customers.length !== 1 ? 's' : ''}
                  {selectedCustomers.length > 0 && (
                    <span className="ml-2 text-green-600 font-medium">
                      ({selectedCustomers.length} sélectionné{selectedCustomers.length !== 1 ? 's' : ''})
                    </span>
                  )}
                </p>
              </div>
              {selectedCustomers.length > 0 && (
                <button
                  onClick={deleteSelectedCustomers}
                  className="px-6 py-2 bg-red-600 text-white text-sm font-light tracking-wide
                           hover:bg-red-700 transition-all duration-300
                           focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2"
                >
                  Supprimer ({selectedCustomers.length})
                </button>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedCustomers.length === customers.length && customers.length > 0}
                      onChange={toggleAllCustomers}
                      className="h-4 w-4 border-gray-300 focus:ring-black cursor-pointer"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-light tracking-wider text-gray-700 uppercase">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-light tracking-wider text-gray-700 uppercase">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-light tracking-wider text-gray-700 uppercase">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-light tracking-wider text-gray-700 uppercase">
                    Review
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-light tracking-wider text-gray-700 uppercase">
                    Amazon
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-light tracking-wider text-gray-700 uppercase">
                    Gifts
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {customers.map((customer) => (
                  <tr
                    key={customer.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedCustomer(customer)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedCustomers.includes(customer.id)}
                        onChange={() => toggleCustomerSelection(customer.id)}
                        className="h-4 w-4 border-gray-300 focus:ring-black cursor-pointer"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-light">
                        {customer.first_name} {customer.last_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">{customer.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">
                        {new Date(customer.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {customer.review_generated ? (
                        <span className="text-sm">
                          {customer.review_stars}★ - {customer.review_tone}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {customer.went_to_amazon ? (
                        <span className="text-sm text-green-600">✓ Yes</span>
                      ) : (
                        <span className="text-sm text-gray-400">✗ No</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {customer.claimed_gifts ? (
                        <span className="text-sm text-green-600">✓ Yes</span>
                      ) : (
                        <span className="text-sm text-gray-400">✗ No</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Customer Detail Modal */}
      {selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6">
          <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-2xl font-light tracking-wide">
                Customer Details
              </h3>
              <button
                onClick={() => setSelectedCustomer(null)}
                className="text-gray-400 hover:text-black transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Customer Info */}
              <div>
                <h4 className="text-sm font-light tracking-wide text-gray-600 mb-2">
                  Customer Information
                </h4>
                <div className="space-y-2">
                  <p className="text-sm">
                    <span className="font-light text-gray-600">Name:</span>{' '}
                    {selectedCustomer.first_name} {selectedCustomer.last_name}
                  </p>
                  <p className="text-sm">
                    <span className="font-light text-gray-600">Email:</span>{' '}
                    {selectedCustomer.email}
                  </p>
                  <p className="text-sm">
                    <span className="font-light text-gray-600">Submitted:</span>{' '}
                    {new Date(selectedCustomer.created_at).toLocaleString()}
                  </p>
                  <p className="text-sm">
                    <span className="font-light text-gray-600">Opted in for surveys:</span>{' '}
                    {selectedCustomer.opt_in_surveys ? 'Yes' : 'No'}
                  </p>
                </div>
              </div>

              {/* Review Data */}
              {selectedCustomer.review_generated && (
                <div>
                  <h4 className="text-sm font-light tracking-wide text-gray-600 mb-2">
                    Review Details
                  </h4>
                  <div className="space-y-2">
                    <p className="text-sm">
                      <span className="font-light text-gray-600">Rating:</span>{' '}
                      {selectedCustomer.review_stars} stars
                    </p>
                    <p className="text-sm">
                      <span className="font-light text-gray-600">Tone:</span>{' '}
                      {selectedCustomer.review_tone}
                    </p>
                    {selectedCustomer.review_text && (
                      <div>
                        <p className="text-sm font-light text-gray-600 mb-2">Generated Review:</p>
                        <div className="bg-gray-50 p-4 border border-gray-200">
                          <p className="text-sm leading-relaxed">{selectedCustomer.review_text}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Journey Status */}
              <div>
                <h4 className="text-sm font-light tracking-wide text-gray-600 mb-2">
                  Journey Status
                </h4>
                <div className="space-y-2">
                  <p className="text-sm">
                    <span className="font-light text-gray-600">Generated Review:</span>{' '}
                    {selectedCustomer.review_generated ? '✓ Yes' : '✗ No'}
                  </p>
                  <p className="text-sm">
                    <span className="font-light text-gray-600">Went to Amazon:</span>{' '}
                    {selectedCustomer.went_to_amazon ? '✓ Yes' : '✗ No'}
                  </p>
                  <p className="text-sm">
                    <span className="font-light text-gray-600">Claimed Gifts:</span>{' '}
                    {selectedCustomer.claimed_gifts ? '✓ Yes' : '✗ No'}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200">
              <button
                onClick={() => setSelectedCustomer(null)}
                className="w-full px-6 py-3 bg-black text-white font-light tracking-wide
                         hover:bg-gray-800 transition-all duration-300
                         focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
